import {
  type Control,
  type FieldPath,
  type FieldValues,
  useFormState,
} from "react-hook-form";
import { FormField } from "~/components/ui/form-field";
import {
  ControlledAdvancedCheckboxGroup,
  ControlledCheckbox,
  ControlledCheckboxGroup,
  ControlledIconPicker,
  ControlledInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledSingleCheckbox,
} from "./controlled-inputs";
import { ControlledFileInput } from "./controlled-file-input";

interface BaseFormFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  required?: boolean;
  helpText?: string;
  isPrivate?: boolean;
}

// Form Input Field
interface FormInputFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function FormInputField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  type = "text",
  placeholder,
  disabled,
}: FormInputFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledInput
        control={control}
        name={name}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
      />
    </FormField>
  );
}

// Form Select Field
interface FormSelectFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  options: string[] | { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}

export function FormSelectField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  options,
  placeholder,
  disabled,
}: FormSelectFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledSelect
        control={control}
        name={name}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
      />
    </FormField>
  );
}

// Form Checkbox Field
interface FormCheckboxFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  children?: React.ReactNode;
  disabled?: boolean;
}

export function FormCheckboxField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  children,
  disabled,
}: FormCheckboxFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      checkbox
    >
      <ControlledCheckbox control={control} name={name} disabled={disabled}>
        {children}
      </ControlledCheckbox>
    </FormField>
  );
}

// Form Checkbox Group Field
interface FormCheckboxGroupFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  options: { value: string; label: string }[];
  disabled?: boolean;
}

export function FormCheckboxGroupField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  options,
  disabled,
}: FormCheckboxGroupFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledCheckboxGroup
        control={control}
        name={name}
        options={options}
        disabled={disabled}
      />
    </FormField>
  );
}

// Form Advanced Checkbox Group Field (with descriptions and custom logic)
interface FormAdvancedCheckboxGroupFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  options: {
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }[];
  onValueChange?: (values: string[], changedValue: string) => void;
  disabled?: boolean;
}

export function FormAdvancedCheckboxGroupField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  options,
  onValueChange,
  disabled,
}: FormAdvancedCheckboxGroupFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledAdvancedCheckboxGroup
        control={control}
        name={name}
        options={options}
        onValueChange={onValueChange}
        disabled={disabled}
      />
    </FormField>
  );
}

// Form Radio Group Field
interface FormRadioGroupFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  options: {
    value: string;
    label: string;
    description?: string;
  }[];
  disabled?: boolean;
}

export function FormRadioGroupField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  options,
  disabled,
}: FormRadioGroupFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledRadioGroup
        control={control}
        name={name}
        options={options}
        disabled={disabled}
      />
    </FormField>
  );
}

// Form Single Checkbox Field (for individual array item checkboxes)
interface FormSingleCheckboxFieldProps<T extends FieldValues>
  extends Omit<BaseFormFieldProps<T>, "label"> {
  value: string;
  currentValues?: string[];
  maxCount?: number;
  _required?: boolean;
  _helpText?: string;
  _isPrivate?: boolean;
  disabled?: boolean;
}

export function FormSingleCheckboxField<T extends FieldValues>({
  control,
  name,
  value,
  currentValues,
  maxCount,
  _required,
  _helpText,
  _isPrivate,
  disabled,
}: FormSingleCheckboxFieldProps<T>) {
  return (
    <ControlledSingleCheckbox
      control={control}
      name={name}
      value={value}
      _currentValues={currentValues}
      maxCount={maxCount}
      disabled={disabled}
    />
  );
}

// Form IconPicker Field
interface FormIconPickerFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  disabled?: boolean;
}

export function FormIconPickerField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  disabled,
}: FormIconPickerFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledIconPicker control={control} name={name} disabled={disabled} />
    </FormField>
  );
}

// Form File Input Field
interface FormFileInputFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  accept?: string;
  maxSize?: number;
  recommendedDimensions?: { width: number; height: number };
  uploadLabel?: string;
  previewType?: "image" | "none";
  placeholder?: string;
  disabled?: boolean;
}

export function FormFileInputField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  helpText,
  isPrivate,
  accept,
  maxSize,
  recommendedDimensions,
  uploadLabel,
  previewType,
  placeholder,
  disabled,
}: FormFileInputFieldProps<T>) {
  const { errors } = useFormState({ control });
  const error = errors[name]?.message;

  return (
    <FormField
      label={label}
      required={required}
      helpText={helpText}
      error={error as string | undefined}
      isPrivate={isPrivate}
    >
      <ControlledFileInput
        control={control}
        name={name}
        accept={accept}
        maxSize={maxSize}
        recommendedDimensions={recommendedDimensions}
        uploadLabel={uploadLabel}
        previewType={previewType}
        placeholder={placeholder}
        disabled={disabled}
      />
    </FormField>
  );
}
